import type { RealtimeEvent } from "@island/realtime-core";
import { useEffect, useRef, useState } from "react";

import {
  createRealtimeClient,
  type ConnectionState,
  type RealtimeClient,
} from "@island/browser-client";

/**
 * One WebSocket for the whole dashboard.
 *
 * Held at the top of the tree rather than per view. A socket per component would
 * open and close one every time an agent clicked between conversations, which costs
 * a handshake and an authentication each time and produces a presence flicker that
 * other participants can see.
 */
export function useRealtime(
  url: string,
  onEvent: (event: RealtimeEvent) => void,
  onUnauthorised: () => void,
): { client: RealtimeClient | null; state: ConnectionState } {
  const [state, setState] = useState<ConnectionState>("closed");
  const clientRef = useRef<RealtimeClient | null>(null);

  // Held in a ref so a changing handler does not tear down and rebuild the socket.
  // The handler closes over React state and therefore changes on nearly every
  // render; reconnecting that often would be indistinguishable from a broken server.
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  const unauthorisedRef = useRef(onUnauthorised);
  unauthorisedRef.current = onUnauthorised;

  useEffect(() => {
    const client = createRealtimeClient({
      url,
      // The dashboard holds no tokens; the session is in an `httpOnly` cookie and
      // the audience names which of the browser's two sessions to read.
      auth: { mode: "cookie", audience: "staff" },
      onEvent: (event) => {
        handlerRef.current(event);
      },
      onStateChange: setState,
      onUnauthorised: () => {
        unauthorisedRef.current();
      },
    });

    clientRef.current = client;
    client.connect();

    return () => {
      client.close();
      clientRef.current = null;
    };
  }, [url]);

  return { client: clientRef.current, state };
}
